import { lighten } from "@wessberg/color";
import { motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import FormButton from "~/components/FormButton";
import FormInputText from "~/components/FormInputText";
import HorizontalRule from "~/components/HorizontalRule";
import { NoticeMsg } from "~/components/Messages";
import Price from "~/components/Price";
import {
  getImageOfSize,
  getProductBySku,
  getProductsAvailabilityBySkus,
  removeBundleFromCart,
  updateMyCart,
} from "~/external/commercetools";
import useCommercetoolsContext from "~/hooks/commercetools/useCommercetoolsContext";
import useFreeShipping from "~/hooks/commercetools/useFreeShipping";
import useCookie from "~/hooks/useCookie";
import useFormInput from "~/hooks/useFormInput";
import { useRect } from "~/hooks/useRect";
import resolveProduct from "~/lib/ctp/resolveProduct";
import { setCart } from "~/store/cart";
import { addToast } from "~/store/toasts";
import theme, { smallerThan } from "~/styles/theme";

const CartWidgetOuterWrapper = styled.div<any>`
  a {
    color: #333;
  }
  ${(p) =>
    p.standalone
      ? `
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-column-gap: 1rem;`
      : `
  position: sticky;
  top: ${p.theme.sizes.topBarHeight}rem;
  box-sizing: border-box;
  border-radius: .2rem;
  overflow: hidden;
  margin-top: 1rem;`}

  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    ${(p) => p.standalone && "grid-template-columns: 1fr;"}
    ${(p) => !p.standalone && "margin-top: 1rem;"}
  }
`;

const CartWidgetInnerWrapper = styled.div<any>`
  ${(p) =>
    !p.standalone &&
    `
      background-color: ${lighten(p.theme.colors.dark, 80)}; padding: 0 1.2rem;
    `}
`;

const CartWidgetHeader = styled.div<any>`
  display: grid;
  grid-template-columns: ${(p) =>
    p.standalone ? "1fr" : p.shouldCollapse ? "1fr 1fr auto" : "1fr auto"};
  align-items: center;
  padding: 1.2rem 0;

  h2 {
    margin: 0;
    ${(p) => p.shouldCollapse && `font-size: small;`}
    text-transform: uppercase;
  }
  ${(p) => p.standalone && "font-size: larger;"}
`;

const CartWidgetProductWrapper = styled.div<any>`
  width: 100%;
  margin-bottom: 1rem;
  img {
    width: ${(p) =>
      p.standalone
        ? p.partOfBundle
          ? "80px"
          : "120px"
        : p.partOfBundle
        ? "50px"
        : "80px"};
    height: ${(p) =>
      p.standalone
        ? p.partOfBundle
          ? "80px"
          : "120px"
        : p.partOfBundle
        ? "50px"
        : "80px"};
    /*background-color: ${(p) => (p.standalone ? "#eee" : "#fff")};*/
    display: block;
    justify-self: center;
    margin-right: 1rem;
  }
  h4 {
    padding: 0;
    margin: 0;
    ${(p) =>
      !p.standalone &&
      `span:nth-child(1) {
      color: #5f5f5f;
      font-size: smaller;
      letter-spacing: 0.2rem;
    }`}
  }
  > div:first-child {
    display: grid;
    grid-template-columns: ${(p) => (p.partOfBundle ? "auto 3fr" : "auto 3fr")};
    align-items: center;
    font-size: ${(p) => (p.partOfBundle ? "smaller" : "inherit")};
  }
  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    img {
      width: ${(p) =>
        p.standalone
          ? p.partOfBundle
            ? "50px"
            : "90px"
          : p.partOfBundle
          ? "20px"
          : "50px"};
      height: ${(p) =>
        p.standalone
          ? p.partOfBundle
            ? "50px"
            : "90px"
          : p.partOfBundle
          ? "20px"
          : "50px"};
    }
  }
`;

const CartWidgetProductBundleWrapper = styled.div<any>`
  width: 100%;
  img {
    width: ${(p) =>
      p.standalone
        ? p.partOfBundle
          ? "80px"
          : "120px"
        : p.partOfBundle
        ? "50px"
        : "80px"};
    height: ${(p) =>
      p.standalone
        ? p.partOfBundle
          ? "80px"
          : "120px"
        : p.partOfBundle
        ? "50px"
        : "80px"};
    // background-color: ${(p) => (p.standalone ? "#eee" : "#fff")};
    display: block;
    border: 0;
    justify-self: center;
    margin-right: 1rem;
  }
  h4 {
    padding: 0;
    margin: 0;
    ${(p) =>
      !p.standalone &&
      `span:nth-child(1) {
      color: #5f5f5f;
      font-size: smaller;
      letter-spacing: 0.2rem;
    }`}
  }
  > div:first-child {
    display: grid;
    grid-template-columns: auto 3fr;
    align-items: center;
    margin-bottom: 1rem;
  }
  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    img {
      width: ${(p) =>
        p.standalone
          ? p.partOfBundle
            ? "50px"
            : "90px"
          : p.partOfBundle
          ? "20px"
          : "50px"};
      height: ${(p) =>
        p.standalone
          ? p.partOfBundle
            ? "50px"
            : "90px"
          : p.partOfBundle
          ? "20px"
          : "50px"};
    }
  }
`;

const CartWidgetProductBundleList = styled.div`
  padding-left: 2rem;
`;

const ItemRemoveButton = styled<any>(FormButton)`
  /*width: 1rem;
  height: 1rem;*/
  font-size: 0.8rem;
  border-radius: 99rem;
  opacity: 0.6;
  padding: 1px 0 0 0;
  margin: 0;
  top: -3px;
  position: relative;
  cursor: pointer;
  font-weight: normal;
  color: unset;
  background-color: transparent;
`;

function CartWidgetProduct(props) {
  const {
    cart,
    item,
    standalone = false,
    partOfBundle = false,
    expandBundle = false,
    removeHandler,
  } = props;

  const { name, quantity, variant, price = {}, hasMultipleVariants = false } = item;

  const { bundle = false, bundleType, bundledProducts = [] } = variant;

  // a01-protective-pouch
  const crossSellSKU = "14001";

  // @ts-ignore
  const countryData = useSelector((store) => store.countryData);

  const ctpCtx = useCommercetoolsContext();
  const dispatch = useDispatch();

  // console.log('item.custom =>', item.custom);
  // console.log('item.variant.attributesRaw =>', item.variant.attributesRaw);
  // console.log('item.variant =>', item.variant);

  // MITIGATE: "Warning: Did not expect server HTML to contain a <div> in <div>"
  // const [crossSellDisplay, setDisplayCrossSell] = useState(false);
  const [crossSellProduct, setCrossSellProduct] = useState(null);
  const [crossSellAvailability, setCrossSellAvailability] = useState(-1);
  const [crossSellAdded, setCrossSellAdded] = useCookie(
    // TODO: item.customFields.BundleId.value might not be unique enough
    `aiaiai-cart-cs-liid-${
      item.lineItemId ||
      item.custom.customFieldsRaw.find((field) => field.name == "BundleId").value
    }`,
    "?"
  );
  useEffect(() => {
    // Skip if ineligible
    if (!variant.key.startsWith("tma-2-")) {
      return;
    }
    if (crossSellAdded !== cart.id) {
      setCrossSellAdded("?");
    }
    const getProductAndCheckAvailability = async () => {
      const [product, availability] = await Promise.all([
        getProductBySku(ctpCtx, crossSellSKU, countryData),
        getProductsAvailabilityBySkus(ctpCtx, [crossSellSKU], countryData),
      ]);
      if (product == null) return;
      const resolvedProduct = await resolveProduct(ctpCtx, product, countryData);
      setCrossSellProduct(
        resolvedProduct.variants.length > 0
          ? resolvedProduct.variants[resolvedProduct.masterVariantIndex]
          : null
      );
      setCrossSellAvailability(availability);
    };
    getProductAndCheckAvailability();
  }, []);

  async function handleCrossSellAdd() {
    const availability = await getProductsAvailabilityBySkus(
      ctpCtx,
      [crossSellSKU],
      countryData
    );

    setCrossSellAvailability(availability);

    // console.log('check availabilty', skus, availability);
    if (availability <= 0) {
      dispatch(
        addToast({
          title: `Error`,
          text: `Product out of stock.`,
          unMountMs: false,
          backgroundColor: "tomato",
          color: "white",
        })
      );
      // setWorking(false);
      return;
    }

    try {
      const updatedCart = await updateMyCart(
        ctpCtx,
        cart,
        [
          {
            addLineItem: {
              sku: crossSellSKU,
              quantity,
              distributionChannel: {
                typeId: "channel",
                id: countryData.channel,
              },
              supplyChannel: {
                typeId: "channel",
                id: countryData.channel,
              },
            },
          },
        ],
        countryData
      );

      dispatch(setCart(updatedCart));
      setCrossSellAdded(cart.id, { expires: 90 });
    } catch (err) {
      console.error(err);
    }
  }

  // const variant = item.variants[item.masterVariantIndex];

  const imageUrl =
    variant && variant.images.length > 0
      ? getImageOfSize(
          variant.images[0].url,
          standalone
            ? partOfBundle
              ? "medium"
              : "large"
            : partOfBundle
            ? "small"
            : "medium"
        )
      : null;
  const imageAlt =
    variant && variant.images.length > 0
      ? variant.images[0].label
      : "No image available";

  /* const bundle =
    //item.__typename === 'CustomLineItem' &&
    item.custom !== null &&
    item.custom.customFieldsRaw !== null &&
    item.custom.customFieldsRaw.some(field => field.name == 'BundleId'); */

  const renderCrossSell = () => {
    if (
      // true || // DISABLED
      !standalone ||
      crossSellAdded !== "?" ||
      !crossSellProduct
    ) {
      return null;
    }
    // console.log('crossSellProduct', crossSellProduct);
    return (
      <motion.div initial={{ opacity: 0, y: "-100%" }} animate={{ opacity: 1, y: 0 }}>
        <HorizontalRule style={{ backgroundColor: "#efefef" }} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "5fr 1fr",
            marginTop: "1rem",
            transitionDuration: ".15s",
          }}
        >
          <div>
            <h4 style={{ display: "inline-block", fontSize: "1rem" }}>
              Add {crossSellProduct.name} for{" "}
              <Price
                {...crossSellProduct.price.value}
                discounted={crossSellProduct.price.discounted?.value}
              />
            </h4>
            <div style={{ fontSize: "smaller" }}>
              {crossSellProduct.attributes?.oneliner || crossSellProduct.description}
            </div>
            <div style={{ fontSize: "smaller" }}>
              <Link href={`/accessories/${crossSellProduct.key}`} passHref>
                <a>Learn more</a>
              </Link>
            </div>
          </div>
          <div
            style={{
              justifySelf: "right",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={handleCrossSellAdd}
          >
            Add
          </div>
        </div>
      </motion.div>
    );
  };

  if (bundle) {
    return (
      <CartWidgetProductBundleWrapper standalone={standalone}>
        <div>
          <img src={imageUrl} alt={imageAlt} />
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: standalone ? "4fr 1fr 1fr" : "3fr 1fr",
              }}
            >
              <h4 style={standalone ? { flex: 1 } : {}}>
                {!standalone && <span>{quantity}x </span>}
                <span>
                  {name}
                  {bundledProducts.find((p) => p.key === "a01__included_free") !==
                    undefined && (
                    <div style={{ fontWeight: "normal", fontSize: "smaller" }}>
                      Incl. A01 Protective Pouch
                    </div>
                  )}
                </span>
              </h4>
              {standalone && (
                <div
                  style={{
                    flex: 1,
                    wordSpacing: ".6rem",
                    textAlign: "center",
                  }}
                >
                  {quantity}
                  {/* TEMP: disabled increase/decrease quantity{' '}
                <FormButton
                  style={{ width: '3rem', borderRadius: '99rem' }}
                  onClick={() =>
                    alert('Please add another from the product page instead.')
                  }
                >
                  +
                </FormButton>{' '}
                <FormButton
                  disabled={quantity <= 1}
                  style={{ width: '3rem', borderRadius: '99rem' }}
                >
                  −
                </FormButton>
                */}
                </div>
              )}

              <div style={{ textAlign: "right" }}>
                {item.lineItemMode === "GiftLineItem" ? (
                  <span>GIFT</span>
                ) : (
                  <Price
                    quantity={quantity}
                    {...variant.price.value}
                    discounted={
                      item.lineItemMode === "GiftLineItem"
                        ? item.totalPrice
                        : variant.price.discounted?.value
                    }
                  />
                )}{" "}
                {standalone && (
                  <div>
                    <ItemRemoveButton onClick={() => removeHandler(item)}>
                      Remove
                    </ItemRemoveButton>
                  </div>
                )}
              </div>
            </div>
            {false && crossSellAvailability > 0 && renderCrossSell()}
          </div>
        </div>
        {expandBundle && (
          <CartWidgetProductBundleList>
            {item.items.map((cartItem, i) => (
              <CartWidgetProduct
                cart={cart}
                partOfBundle
                item={cartItem}
                standalone={standalone}
                key={`item-${i}`}
              />
            ))}
          </CartWidgetProductBundleList>
        )}
      </CartWidgetProductBundleWrapper>
    );
  }
  return (
    <CartWidgetProductWrapper standalone={standalone} partOfBundle={partOfBundle}>
      <div>
        <img src={imageUrl} alt={imageAlt} />
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: standalone ? "4fr 1fr 1fr" : "3fr 1fr",
            }}
          >
            <h4 style={standalone ? { flex: 1 } : {}}>
              {!standalone && <span>{quantity}x </span>}
              <span>
                {name}
                {"digital-asset" in item.variant.attributes && (
                  <div style={{ fontWeight: "normal", fontSize: "smaller" }}>
                    Digital download
                  </div>
                )}
              </span>
              {hasMultipleVariants && (
                <>
                  {variant.color || variant.size || variant.powerPlugType ? (
                    <>
                      {variant.powerPlugType && (
                        <span
                          style={{
                            fontWeight: "normal",
                            textTransform: "capitalize",
                          }}
                        ></span>
                      )}
                      {variant.color && (
                        <span
                          style={{
                            fontWeight: "normal",
                            textTransform: "capitalize",
                          }}
                        >
                          {" "}
                          · {variant.color.name}
                        </span>
                      )}
                      {variant.size && (
                        <span
                          style={{
                            fontWeight: "normal",
                            textTransform: "capitalize",
                          }}
                        >
                          {" "}
                          · {variant.size.label}
                        </span>
                      )}
                    </>
                  ) : (
                    <span
                      style={{
                        fontWeight: "normal",
                        textTransform: "capitalize",
                      }}
                    >
                      {" "}
                      · {variant.key}
                    </span>
                  )}
                </>
              )}
            </h4>
            {standalone && (
              <div
                style={{
                  flex: 1,
                  wordSpacing: ".6rem",
                  textAlign: "center",
                  // alignSelf: 'center'
                }}
              >
                {quantity}
                {/* TEMP: disabled increase/decrease quantity
                {' '}
                <FormButton style={{ width: '3rem', borderRadius: '99rem' }}>
                  +
                </FormButton>{' '}
                <FormButton
                  disabled={quantity <= 1}
                  style={{ width: '3rem', borderRadius: '99rem' }}
                >
                  −
                </FormButton>
                */}
              </div>
            )}

            {!partOfBundle && (
              <div style={{ textAlign: "right" }}>
                {item.lineItemMode === "GiftLineItem" ? (
                  <span>GIFT</span>
                ) : (
                  <Price
                    quantity={quantity}
                    {...price.value}
                    discounted={
                      item.lineItemMode === "GiftLineItem"
                        ? item.totalPrice
                        : price.discounted?.value
                    }
                  />
                )}{" "}
                {standalone && item.lineItemMode !== "GiftLineItem" && (
                  <div>
                    <ItemRemoveButton onClick={() => removeHandler(item)}>
                      Remove
                    </ItemRemoveButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </CartWidgetProductWrapper>
  );
}

const PriceLine = styled.div<any>`
  display: flex;
  line-height: 1.6;
  font-weight: ${(p) => (p.total ? "bold" : "normal")};
  font-size: ${(p) => (p.total ? "20px" : p.tax ? "14px" : "16px")};
  margin: ${(p) => (p.total ? "0.5rem" : "0")} 0;
  div:first-child {
    flex: 1;
  }
  opacity: ${(p) => (p.tax ? 0.6 : 1)};
  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    /*font-size: ${(p) => (p.total ? "20px" : p.tax ? "10px" : "12px")};*/
  }
`;

const PriceSummaryWrapper = styled.div<any>`
  padding-bottom: 1.2rem;

  ${(p) =>
    p.standalone &&
    `
  display: grid;
  grid-template-columns: 1fr 2fr;`}
  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    grid-template-columns: 1fr;
  }
`;

function PriceSummary(props) {
  const { children, currencyCode, centAmount, total, fractionDigits, ...restOfProps } =
    props;

  return (
    <PriceLine {...restOfProps} total={total}>
      <div>{children}</div>
      <div>
        {centAmount === 0 ? (
          <span>FREE</span>
        ) : (
          <Price
            currencyCode={currencyCode}
            centAmount={centAmount}
            fractionDigits={fractionDigits}
            currencyStyle={total && { fontWeight: "normal", fontSize: "smaller" }}
          />
        )}
      </div>
    </PriceLine>
  );
}

function Tax(props) {
  const { portions } = props;
  // const { amount, name }( = props;

  return (
    <>
      {portions.map((tax, i) => (
        <PriceLine style={{ fontSize: "smaller", color: "#333" }} key={i} tax>
          <div>
            Including {tax.name} of{" "}
            <Price
              currencyCode={tax.amount.currencyCode}
              centAmount={tax.amount.centAmount}
              fractionDigits={tax.amount.fractionDigits}
              currencyStyle={{ fontSize: "inherit" }}
            />
          </div>
        </PriceLine>
      ))}
    </>
  );
}

const cartRef = React.createRef<HTMLDivElement>();

/**
 * Component
 */
function CartWidget(props) {
  let { children, cart = null, standalone = false } = props;

  // @ts-ignore
  const countryData = useSelector((store) => store.countryData);
  // @ts-ignore
  cart = useSelector((store) => store.cart);

  // still null... i dunno
  if (cart === null) return null;

  // console.log("CartWidget", cart.id, cart.version, cart);
  // console.log(cart.cartItems);

  const productDiscountCentAmount = cart.cartItems.reduce((n, cartItem) => {
    n += cartItem.price.value.centAmount - cartItem.totalPrice.centAmount;
    return n;
  }, 0);

  const produceDiscount = {
    centAmount: productDiscountCentAmount,
    currencyCode: countryData.currency,
  };

  // console.log('productDiscountCentAmount', productDiscountCentAmount);

  // console.log('CART =>', JSON.stringify(cart, null, 2));

  // @ts-ignore
  const shippingMethods = useSelector((store) => store.shippingMethods);

  const ctpCtx = useCommercetoolsContext();
  const dispatch = useDispatch();

  /* if (!cartItems) {
    cartItems = useCartItems(cart);
  } */
  // const cartItems = useCartItems(cart);
  const { cartItems } = cart;

  // console.log('cartItems =>', cartItems);

  const isCartEmpty = cartItems.length === 0;

  /* if (cartItems.length === 0) {
    return <div>Cart is empty...</div>;
  } */

  const router = useRouter();

  const [discountCodeErrorDismissed, setDiscountCodeErrorDismissed] = useState(false);
  const [showPromotionCodeField, setShowPromotionCodeField] = useState(false);
  const [applyingPromotionCode, setApplyingPromotionCode] = useState(false);
  const [checkingDiscountCodes, setCheckingDiscountCodes] = useState(true);
  const [checkedDiscountCodes, setCheckedDiscountCodes] = useState(false);
  const [removedDiscountCodes, setRemovedDiscountCodes] = useState<
    { typeId: string; id: string; code: string }[]
  >([]);
  const promotionCode = useFormInput("");

  function checkDiscountCodes() {
    // Remove any inactive discount codes

    let updatedCart: any;
    async function processAsync(cartToUpdate) {
      // console.log("recalculateCart cart version", cartToUpdate.version);
      updatedCart = await recalculateCart(cartToUpdate);

      const removeDiscountCodes = removedDiscountCodes;

      for (const code of updatedCart.discountCodes) {
        // console.log("code", code.state, code);
        if (
          code.state !== "MatchesCart" &&
          !removedDiscountCodes.some((c) => c.id === code.discountCode.id)
        ) {
          // NotActive, NotValid, DoesNotMatchCart,
          // MaxApplicationReached, ApplicationStoppedByPreviousDiscount
          removeDiscountCodes.push({
            code: code.discountCode.code,
            id: code.discountCode.id,
            typeId: code.discountCodeRef.typeId,
          });
        }
      }

      if (removeDiscountCodes.length > 0) {
        // Remove discount codes and recalculate cart
        updatedCart = await handleRemoveDiscountCodes(updatedCart, removeDiscountCodes);
        setRemovedDiscountCodes(removeDiscountCodes);
      }

      setCheckingDiscountCodes(false);
    }
    processAsync(cart);
  }

  useEffect(() => {
    if (checkedDiscountCodes) return;
    checkDiscountCodes();
    setCheckedDiscountCodes(true);
  }, []);

  async function recalculateCart(cartToUpdate) {
    try {
      const updatedCart = await updateMyCart(
        ctpCtx,
        cartToUpdate,
        [
          {
            recalculate: {
              updateProductData: false,
            },
          },
        ],
        countryData
      );
      updatedCart.recalculated = new Date();
      // console.log("recalculated cart", updatedCart);
      dispatch(setCart(updatedCart));
      return updatedCart;
    } catch (e) {
      console.log(e);
      return cartToUpdate;
    }
  }

  async function handleAddDiscountCode(e) {
    if (!promotionCode.value) {
      return;
    }
    setApplyingPromotionCode(true);
    try {
      let updatedCart = await updateMyCart(
        ctpCtx,
        cart,
        [
          {
            addDiscountCode: {
              code: `${promotionCode.value.toUpperCase()}`,
            },
          },
        ],
        countryData
      );
      // console.log("added discount code", updatedCart);

      // update potential new line item if it's a gifted item
      const giftedLineItems = updatedCart.lineItems.filter(
        (li) =>
          li.lineItemMode === "GiftLineItem" &&
          !li.supplyChannel &&
          !li.distributionChannel
      );

      // console.log("giftedLineItems", giftedLineItems);

      if (giftedLineItems.length > 0) {
        const actions = [];
        for (const giftedLineItem of giftedLineItems) {
          actions.push(
            {
              setLineItemDistributionChannel: {
                lineItemId: giftedLineItem.id,
                distributionChannel: {
                  typeId: "channel",
                  id: countryData.channel,
                },
              },
            },
            {
              setLineItemSupplyChannel: {
                lineItemId: giftedLineItem.id,
                supplyChannel: {
                  typeId: "channel",
                  id: countryData.channel,
                },
              },
            }
          );
        }
        // console.log('actions', actions);
        updatedCart = await updateMyCart(ctpCtx, updatedCart, actions, countryData);
        // console.log("updated line items", updatedCart);
      }

      // clear the input
      promotionCode.onChange({
        target: {
          value: "",
        },
      });
      updatedCart.updatedDiscountCodes = new Date();
      dispatch(setCart(updatedCart));
      setApplyingPromotionCode(false);
    } catch (e) {
      console.log(e);
      setApplyingPromotionCode(false);
    }
  }

  async function handleRemoveDiscountCodes(updateThisCart, discountCodes) {
    // console.log("remove discount codes + recalculate", discountCodes);
    if (discountCodes.length === 0) {
      return updateThisCart;
    }
    try {
      const updatedCart = await updateMyCart(
        ctpCtx,
        updateThisCart,
        [
          ...discountCodes.map((code) => ({
            removeDiscountCode: {
              discountCode: { id: code.id, typeId: code.typeId },
            },
          })),
          {
            recalculate: {
              updateProductData: false,
            },
          },
        ],
        countryData
      );
      updatedCart.recalculated = new Date();
      dispatch(setCart(updatedCart));
      return updatedCart;
    } catch (err) {
      console.log(err);
      return updateThisCart;
    }
  }

  async function handleRemoveDiscountCode(discountCodeRef) {
    // console.log("remove discount code", discountCodeRef);
    const { id, typeId } = discountCodeRef;
    try {
      const updatedCart = await updateMyCart(
        ctpCtx,
        cart,
        [
          {
            removeDiscountCode: {
              discountCode: { id, typeId },
            },
          },
        ],
        countryData
      );
      updatedCart.updatedDiscountCodes = new Date();
      dispatch(setCart(updatedCart));
    } catch (err) {
      console.log(err);
    }
  }

  async function handleRemoveLineItemFromCart(item) {
    // console.log(
    //   "remove item",
    //   item.lineItemId,
    //   item.quantity,
    //   !item.custom,
    //   item.custom && !item.custom.customFields
    // );

    try {
      let updatedCart;
      if (item.custom && item.custom.customFieldsRaw) {
        const bundleId = item.custom.customFieldsRaw.find(
          (field) => (field.name = "BundleId")
        );
        if (bundleId) {
          // console.log("REMOVE BUNDLE", bundleId.value);
          updatedCart = await removeBundleFromCart(
            ctpCtx,
            cart,
            bundleId.value,
            countryData
          );
        }
      } else {
        updatedCart = await updateMyCart(
          ctpCtx,
          cart,
          [
            {
              removeLineItem: {
                lineItemId: item.lineItemId,
                quantity: item.quantity,
              },
            },
          ],
          countryData
        );
      }
      // console.log(updatedCart);
      dispatch(setCart(updatedCart));
    } catch (err) {
      console.log(err);
    }
  }

  function renderProductDiscountField(price = { centAmount: 0, currencyCode: "DKK" }) {
    if (price.centAmount === 0) {
      return null;
    }
    return (
      <PriceLine style={{ fontWeight: "bold" }}>
        <div>Discount</div>
        <div>
          <span style={{ color: "red" }}>
            <span style={{ fontSize: "small" }}>{price.currencyCode}</span> -
            {price.centAmount / 100}
          </span>
        </div>
      </PriceLine>
    );
  }

  function renderPromotionField() {
    if (checkingDiscountCodes) return;
    return (
      <>
        {cart.discountCodes.length > 0 ? (
          <div
            style={{
              transitionDuration: ".15s",
              marginTop: "1rem",
            }}
          >
            {cart.discountCodes.map((discountCode) => {
              const prices = discountCode.discountCode.cartDiscounts.map(
                (cartDiscount, index) => {
                  if (cartDiscount.value.type === "absolute") {
                    const absoluteDiscount = cartDiscount.value.money.find(
                      (money) => money.currencyCode === countryData.currency
                    );
                    return (
                      <span
                        style={{ display: "block", textAlign: "right" }}
                        key={`${discountCode.discountCode.code}-${index}`}
                      >
                        -<Price {...absoluteDiscount} />
                      </span>
                    );
                  }
                  if (
                    cartDiscount.value.type === "relative" &&
                    cartDiscount.key !== "free-shipping"
                  ) {
                    return (
                      <span
                        style={{ display: "block", textAlign: "right" }}
                        key={`${discountCode.discountCode.code}-${index}`}
                      >{`-${cartDiscount.value.permyriad / 100}%`}</span>
                    );
                  }
                }
              );

              return (
                <PriceLine
                  style={{ fontWeight: "bold" }}
                  key={discountCode.discountCode.code}
                >
                  <div>
                    Promotion{" "}
                    <span style={{ fontSize: "small" }}>
                      ({discountCode.discountCode.code})
                    </span>
                  </div>
                  <div>
                    <div style={{ color: "red" }}>{prices}</div>
                    <div style={{ textAlign: "right" }}>
                      <ItemRemoveButton
                        onClick={() =>
                          handleRemoveDiscountCode(discountCode.discountCodeRef)
                        }
                      >
                        remove
                      </ItemRemoveButton>
                    </div>
                  </div>
                </PriceLine>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              placeItems: "center left",
              height: "60px",
              margin: standalone ? ".6rem 0" : ".3rem 0",
              transitionDuration: ".15s",
            }}
          >
            {showPromotionCodeField ? (
              <div
                style={{
                  display: "grid",
                  gridColumnGap: "1rem",
                  gridTemplateColumns: "2fr 1fr",
                  // maxWidth: '30rem',
                  alignContent: "right",
                }}
              >
                <FormInputText
                  inline
                  name="discountCode"
                  label="Promotion code"
                  {...promotionCode}
                  disabled={applyingPromotionCode}
                  focus
                />
                <FormButton
                  style={{
                    display: "inline-block",
                    height: "60px",
                  }}
                  onClick={handleAddDiscountCode}
                  disabled={applyingPromotionCode}
                >
                  {applyingPromotionCode ? "···" : "Add"}
                </FormButton>
              </div>
            ) : (
              <div>
                <a
                  style={{ fontSize: "small" }}
                  href="#show-promo-field"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowPromotionCodeField(true);
                    return false;
                  }}
                >
                  Add promotion code
                </a>
              </div>
            )}
          </div>
        )}
        {!discountCodeErrorDismissed && removedDiscountCodes.length > 0 && (
          <NoticeMsg backgroundColor={theme.colors.dark}>
            The discount {removedDiscountCodes.length > 1 ? "codes" : "code"}{" "}
            {removedDiscountCodes.map((dc, index) => (
              <>
                <strong>{dc.code}</strong>
                {removedDiscountCodes.length > 2 &&
                index < removedDiscountCodes.length - 2
                  ? ", "
                  : removedDiscountCodes.length > 1 &&
                    index < removedDiscountCodes.length - 1
                  ? " and "
                  : index < removedDiscountCodes.length - 1
                  ? ", "
                  : ""}
              </>
            ))}{" "}
            {removedDiscountCodes.length > 1 ? "are" : "is"} no longer valid and has
            been removed.{" "}
            <a
              className="dismiss"
              href="#"
              onClick={() => {
                setDiscountCodeErrorDismissed(true);
              }}
            >
              ✕
            </a>
          </NoticeMsg>
        )}
      </>
    );
  }

  const shippingRate = useFreeShipping();

  function calculateSubtotal() {
    if (!cartItems || cartItems.length === 0) {
      return false;
    }

    // console.log("calculateSubtotal", cartItems);

    const firstItem = cartItems[0].price.discounted
      ? cartItems[0].price.discounted.value
      : cartItems[0].price.value;
    return {
      currencyCode: firstItem.currencyCode,
      centAmount: cartItems
        .map(
          (item) =>
            (item.variant.price.discounted
              ? item.variant.price.discounted.value.centAmount
              : item.lineItemMode === "GiftLineItem"
              ? item.totalPrice.centAmount
              : item.variant.price.value.centAmount) * item.quantity
        )
        .reduce((accumulator, value) => accumulator + value),
      fractionDigits: firstItem.fractionDigits,
    };
  }
  const subtotal = calculateSubtotal();

  const cartRect = useRect(cartRef);
  const isMobile = cartRect.left + cartRect.right <= theme.breakpoints.small;
  const [collapsed, setCollapsed] = useState(true);
  const shouldCollapse = !standalone && collapsed && isMobile;
  const itemCount = cartItems.map((item) => item.quantity).reduce((a, b) => a + b, 0);

  const crossSells = []; /* [
    {
      key: 'a01-protective-pouch',
      sku: '01401',
      name: 'A01 - Protective pouch',
      path: '/accessories/a01-protective-pouch',
      price: {
        value: {
        centAmount: 15000,
        currencyCode: 'DKK',
        fractionDigits: 2,
        },
        discounted: null
      }
    }
  ]; */
  return (
    <CartWidgetOuterWrapper standalone={standalone} ref={cartRef}>
      {isCartEmpty ? (
        <div style={{ display: "grid", placeItems: "center" }}>
          <div>Your cart is empty.</div>
        </div>
      ) : (
        <CartWidgetInnerWrapper
          standalone={standalone}
          onClick={(e) => {
            e.preventDefault();
            // if (isMobile) {
            //   setCollapsed(!collapsed);
            // }
          }}
        >
          <CartWidgetHeader standalone={standalone} shouldCollapse={shouldCollapse}>
            {children}
            {isMobile && !standalone && collapsed && (
              <>
                <a
                  href="#expand"
                  onClick={(e) => {
                    e.preventDefault();
                    if (isMobile) {
                      setCollapsed(!collapsed);
                    }
                  }}
                >
                  Show order summary
                </a>

                <PriceSummary
                  {...cart.taxedPrice.totalGross}
                  total
                  style={{ fontSize: "medium", margin: 0 }}
                ></PriceSummary>
              </>
            )}
            {isMobile && !standalone && !collapsed && (
              <>
                <a
                  href="#expand"
                  onClick={(e) => {
                    e.preventDefault();
                    if (isMobile) {
                      setCollapsed(!collapsed);
                    }
                  }}
                >
                  Hide order summary
                </a>
                <PriceSummary
                  {...cart.taxedPrice.totalGross}
                  total
                  style={{ fontSize: "medium", margin: 0 }}
                ></PriceSummary>
              </>
            )}
          </CartWidgetHeader>
          <div
            style={{
              display: shouldCollapse ? "none" : "block",
              transitionDuration: "2.15s",
            }}
          >
            {cartItems.map((cartItem, i) => (
              <Fragment key={`item-${i}`}>
                <CartWidgetProduct
                  cart={cart}
                  item={cartItem}
                  standalone={standalone}
                  removeHandler={handleRemoveLineItemFromCart}
                  // expandBundle
                />
                {
                  /* crossSells.length > 0 && cartItems.length - 1 ===  */
                  standalone && cartItems.length - 1 !== i ? (
                    <HorizontalRule style={{ backgroundColor: "#efefef" }} />
                  ) : (
                    <div style={{ height: "1.2rem" }} />
                  )
                }
              </Fragment>
            ))}
            {crossSells.map((crossSell, i) => (
              <Fragment key={`cross-sell-${i}`}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 3fr" }}>
                  <div>{/* IMAGE? */}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "4fr 1fr" }}>
                    <div>
                      <h4 style={{ display: "inline-block", fontSize: "1rem" }}>
                        Add {crossSell.name} for{" "}
                        <Price
                          centAmount={crossSell.price.value.centAmount}
                          currencyCode={crossSell.price.value.currencyCode}
                          fractionDigits={crossSell.price.value.fractionDigits}
                          discounted={crossSell.discounted}
                        />
                      </h4>
                      <div style={{ fontSize: "smaller" }}>
                        Designed to protect your headphones from dust and scratches.
                      </div>
                      <div style={{ fontSize: "smaller" }}>
                        <Link href={crossSell.path} passHref>
                          <a>Learn more</a>
                        </Link>
                      </div>
                    </div>

                    <div
                      style={{
                        justifySelf: "right",
                        alignSelf: "center",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      // onClick={handleCrossSellAdd}
                    >
                      Add
                    </div>
                  </div>
                </div>
                {2 - 1 !== i ? (
                  <HorizontalRule style={{ backgroundColor: "#efefef" }} />
                ) : (
                  <div style={{ height: "1.2rem" }} />
                )}
              </Fragment>
            ))}
            <PriceSummaryWrapper standalone={standalone}>
              {standalone && <div>{/* SPACER */}</div>}
              <div>
                <PriceSummary {...subtotal}>Subtotal</PriceSummary>

                {/* {renderProductDiscountField(produceDiscount)} */}
                {cart.shippingInfo ? (
                  <PriceSummary
                    style={{ marginTop: "0.5rem" }}
                    {...cart.shippingInfo.taxedPrice.totalGross}
                  >
                    Shipping{" "}
                    {cart.shippingInfo && (
                      <span style={{ fontSize: "small" }}>
                        ({cart.shippingInfo.shippingMethodName})
                      </span>
                    )}
                  </PriceSummary>
                ) : (
                  <PriceLine style={{ marginTop: "0.5rem" }}>
                    <div>Shipping</div>
                    <div>
                      {shippingRate &&
                      cart.taxedPrice.totalGross.centAmount >= shippingRate.centAmount
                        ? "FREE"
                        : "~"}
                    </div>
                  </PriceLine>
                )}
                {renderPromotionField()}
                <PriceSummary {...cart.taxedPrice.totalGross} total>
                  Order total
                </PriceSummary>
                {/* TODO: Make generic through @aiaiai/shipping-countries */}
                {countryData.code == "GB" ? (
                  <PriceLine style={{ fontSize: "smaller", color: "#333" }}>
                    <div>Including UK VAT, import duties, and taxes</div>
                  </PriceLine>
                ) : (
                  <Tax portions={cart.taxedPrice.taxPortions} />
                )}
              </div>
            </PriceSummaryWrapper>
          </div>
        </CartWidgetInnerWrapper>
      )}
    </CartWidgetOuterWrapper>
  );
}

export default CartWidget;
